import React from 'react';
import { motion } from 'framer-motion';
import { SparklesIcon, HomeIcon, Building2Icon } from 'lucide-react';
import { LightingCategoryCard } from '../components/christmas/LightingCategoryCard';
import { DecorationServicesSection } from '../components/christmas/DecorationServicesSection';
import { ChristmasContactSection } from '../components/christmas/ChristmasContactSection';
import { EstimateFormSection } from '../components/christmas/estimate/EstimateFormSection';
import { christmasHeroImage, lightingCategories } from '../data/christmasLighting';
import { christmasQuoteHref } from '../data/christmasContacts';
const ease: [number, number, number, number] = [0.22, 1, 0.36, 1];
export function ChristmasLights() {
  return <main id="main" className="w-full bg-cream" data-id="element-249" data-mp="39a9gfygxbd45">
      <section aria-labelledby="christmas-title" className="relative isolate overflow-hidden bg-forest-900" data-id="element-250" data-mp="13h2klsukbfl4">
        <img src={christmasHeroImage} alt="Elegant home at night decorated with roofline lights, wreaths, garlands, lit bushes and trees, and stake lights" className="absolute inset-0 -z-20 h-full w-full object-cover object-[65%_center]" data-id="element-251" data-mp="17io2jslyek26" />
        <div aria-hidden="true" className="absolute inset-0 -z-10 bg-forest-900/55" data-id="element-252" data-mp="1tthtj485ooia" />
        <div aria-hidden="true" className="absolute inset-y-0 left-0 -z-10 w-full bg-forest-900/30 lg:w-3/5" data-id="element-253" data-mp="1tvc69v99xaba" />

        <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 md:py-36" data-id="element-254" data-mp="114u2st4n16xc">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          ease
        }} className="max-w-3xl" data-id="element-255" data-mp="116wudhlfv846">
            <p className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-[#EAD9A8] ring-1 ring-white/20 backdrop-blur-sm" data-id="element-256" data-mp="1b0x9mcafptqm">
              <SparklesIcon className="h-4 w-4" aria-hidden="true" data-id="element-257" data-mp="2mk9pq1p81d5m" />
              Holiday Season
            </p>
            <h1 id="christmas-title" className="mt-7 font-display text-5xl font-medium leading-[1.02] tracking-tight text-white sm:text-6xl lg:text-7xl" data-id="element-258" data-mp="2hletny28ts3k">
              Chacon’s Lawncare <span className="italic text-[#EAD9A8]" data-id="element-259" data-mp="2ykpufx8h3oxl">Christmas Decoration</span> Services
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-white/85 md:text-xl" data-id="element-260" data-mp="33j9ko9u3nmvr">
              Professional Christmas Light Design &amp; Installation
            </p>
            <div className="mt-10 flex flex-col gap-3 sm:flex-row" data-id="element-261" data-mp="25ty1r9qm9if0">
              <a href={christmasQuoteHref} className="inline-flex h-14 items-center justify-center rounded-full bg-olive px-8 text-[13px] font-bold uppercase tracking-[0.08em] text-white shadow-[0_10px_30px_-12px_rgba(0,0,0,0.6)] transition-colors hover:bg-olive-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage focus-visible:ring-offset-2 focus-visible:ring-offset-forest-900" data-id="element-262" data-mp="2avdkgv5bncu8">
                Get a Free Quote
              </a>
              <a href="#decorations" className="inline-flex h-14 items-center justify-center rounded-full bg-white/5 px-8 text-[13px] font-bold uppercase tracking-[0.08em] text-white ring-1 ring-white/50 backdrop-blur-sm transition-colors hover:bg-white/15" data-id="element-263" data-mp="98gn08ki5qck">
                View Decorations
              </a>
            </div>
          </motion.div>
        </div>
        <div aria-hidden="true" className="flex h-1.5 w-full" data-id="element-264" data-mp="3mhw6toz3xtcr">
          <span className="flex-1 bg-olive" data-id="element-265" data-mp="3ho0b6t6bgikr" />
          <span className="flex-1 bg-[#9B2335]" data-id="element-266" data-mp="3hn8etg4wagtf" />
          <span className="flex-1 bg-[#EAD9A8]" data-id="element-267" data-mp="3doj4jwf5lfe0" />
        </div>
      </section>

      <section id="lighting-options" aria-labelledby="options-title" className="scroll-mt-32 py-20 md:py-28" data-id="element-268" data-mp="38ows3p75g369">
        <div className="mx-auto max-w-7xl px-5 sm:px-8" data-id="element-269" data-mp="33pjwaeyavlrj">
          <div className="mx-auto max-w-2xl text-center" data-id="element-270" data-mp="33r4pa04yuwna">
            <p className="inline-flex items-center gap-3 text-xs font-bold uppercase tracking-[0.22em] text-olive" data-id="element-271" data-mp="2yric1qp51geh">
              <span aria-hidden="true" className="h-px w-8 bg-current" data-id="element-272" data-mp="12nuedrlqq069" />
              Choose Your Property Type
              <span aria-hidden="true" className="h-px w-8 bg-current" data-id="element-273" data-mp="17o8gkt0nufwd" />
            </p>
            <h2 id="options-title" className="mt-5 font-display text-4xl font-medium leading-tight tracking-tight text-forest md:text-5xl" data-id="element-274" data-mp="swzidviozm7m">
              Lighting for homes <span className="italic text-olive" data-id="element-275" data-mp="jwbazjnt0k3p">&amp;</span> businesses.
            </h2>
            <div className="mt-6 flex items-center justify-center gap-6 text-sm font-semibold text-forest/70" data-id="element-276" data-mp="ow79529tlhbc">
              <span className="flex items-center gap-2" data-id="element-277" data-mp="9zghjf6e19wa">
                <HomeIcon className="h-4 w-4 text-olive" aria-hidden="true" data-id="element-278" data-mp="exhynuk96z54" /> Residential
              </span>
              <span aria-hidden="true" className="h-4 w-px bg-forest/20" data-id="element-279" data-mp="24co4kko4gh7d" />
              <span className="flex items-center gap-2" data-id="element-280" data-mp="24abrolx80x16">
                <Building2Icon className="h-4 w-4 text-olive" aria-hidden="true" data-id="element-281" data-mp="29ag7e9qb1nqc" /> Commercial
              </span>
            </div>
          </div>

          <ul className="mt-14 grid gap-8 md:grid-cols-2" data-id="element-282" data-mp="3tr7762uukmm1">
            {lightingCategories.map((category, index) => <motion.li key={category.slug} initial={{
            opacity: 0,
            y: 24
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.6,
            ease,
            delay: index * 0.1
          }} data-id="element-283" data-mp="7dp8wp1qwk45" data-mpi={index}>
                <LightingCategoryCard category={category} data-id="element-284" data-mp="cdlc4m2mc7in" />
              </motion.li>)}
          </ul>
        </div>
      </section>

      <DecorationServicesSection data-id="element-285" data-mp="qc1sz5u73eei" />

      <EstimateFormSection data-id="element-286" data-mp="ldqh6pkcattn" />

      <ChristmasContactSection data-id="element-287" data-mp="ldgogu7wu8k9" />
    </main>;
}